import type {
  AuditEventDto,
  AuditListResponse,
  AuditQuery,
  AuditVerifyResponse,
} from '@fruuit/shared'
import type { QueryParams } from '../../api/client'
import { useApiQuery } from '../../api/hooks'

export const auditKeys = {
  all: ['audit'] as const,
  list: (query: AuditQuery) => ['audit', 'list', query] as const,
  event: (chain: string, seq: number) => ['audit', 'event', chain, seq] as const,
  verify: (chain: string) => ['audit', 'verify', chain] as const,
}

/** `GET /audit` (P-AUD-002): newest first, filtered, paginated. */
export function useAuditList(query: AuditQuery) {
  const params: QueryParams = {}
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params[key] = value as string | number
  }
  return useApiQuery<AuditListResponse>(auditKeys.list(query), '/audit', {
    query: params,
    placeholderData: (previous) => previous,
  })
}

/** `GET /audit/:chain/:seq`: one event, re-read from the server. */
export function useAuditEvent(chain: string | null, seq: number | null) {
  return useApiQuery<AuditEventDto>(
    auditKeys.event(chain ?? '', seq ?? 0),
    `/audit/${encodeURIComponent(chain ?? '-')}/${seq ?? 0}`,
    { enabled: chain !== null && seq !== null },
  )
}

/** `GET /audit/verify?chain=`: recompute the chain (P-AUD-003). Refetch to verify again. */
export function useAuditVerify(chain: string | null) {
  return useApiQuery<AuditVerifyResponse>(auditKeys.verify(chain ?? ''), '/audit/verify', {
    query: { chain },
    enabled: Boolean(chain),
    staleTime: 60_000,
  })
}
